/**
 * Step 3 — meeting details (branch + date + time).
 *
 * Mirrors `rae-booking-step3-details.html`. a11y highlights:
 *   - Branch picker is a `role="radiogroup"` with real button cards.
 *   - Calendar grid is a proper table-like grid with arrow-key navigation:
 *     ArrowLeft / ArrowRight cycle days, ArrowUp/Down jump 7. Enter selects.
 *   - Time slot chips are `<button>` elements; disabled slots set `aria-disabled`.
 *   - Limited-availability slots show icon + text ("Few left") — not color alone.
 */

import { useEffect, useMemo, useRef, useState, type Dispatch, type KeyboardEvent } from 'react';
import type { BookingAction } from '../wizardState';
import type { RaeBranchChoice, BookingWizardState } from '../types';
// RAE_STATIC_BRANCHES removed in v0.1.26 — config.branches is the only
// source of truth now.
import { Button } from '@/components/primitives';
import { useConfig } from '@/contexts/ConfigContext';

export interface Step3DetailsProps {
  state: BookingWizardState;
  dispatch: Dispatch<BookingAction>;
  onContinue: () => void;
  onBack: () => void;
  onDeflect?: () => void;
}

/** ISO yyyy-mm-dd. */
function toIso(year: number, monthIndex: number, day: number): string {
  return `${year}-${`${monthIndex + 1}`.padStart(2, '0')}-${`${day}`.padStart(2, '0')}`;
}

/** Generate mock availability for a month — weekdays 21+ of the current month are "has slots". */
function hasSlotsOn(iso: string): boolean {
  const d = new Date(`${iso}T12:00:00`);
  const dow = d.getDay();
  if (dow === 0 || dow === 6) return false;
  return d.getDate() >= 21;
}

const MORNING = ['08:30', '09:00', '09:30', '10:00', '10:30', '11:00'];
const AFTERNOON = ['13:00', '14:00', '14:30', '15:00', '15:30', '16:00'];
const EVENING = ['17:00', '17:30'];
const UNAVAILABLE = new Set(['08:30', '15:30']);
const LIMITED = new Set(['09:00', '17:00']);

function formatTime12(hhmm: string): string {
  const parts = hhmm.split(':');
  const h24Str = parts[0] ?? '0';
  const mStr = parts[1] ?? '00';
  const h24 = Number.parseInt(h24Str, 10);
  const h12 = ((h24 + 11) % 12) + 1;
  const ampm = h24 >= 12 ? 'PM' : 'AM';
  return `${h12}:${mStr} ${ampm}`;
}

export function Step3Details({ state, dispatch, onContinue, onBack, onDeflect }: Step3DetailsProps): JSX.Element {
  const { config } = useConfig();
  const now = new Date();
  const [viewYear, setViewYear] = useState<number>(now.getFullYear());
  const [viewMonth, setViewMonth] = useState<number>(now.getMonth());
  const gridRef = useRef<HTMLDivElement | null>(null);

  const branches: ReadonlyArray<RaeBranchChoice> = config.branches.map((b) => ({
    branchCode: b.branchCode,
    branchName: b.branchName,
    addressLine1: b.addressLine1 ?? '',
    city: b.city ?? '',
    state: b.state ?? '',
    zip: b.zip ?? '',
  }));

  const firstDayOffset = useMemo(() => {
    const first = new Date(viewYear, viewMonth, 1);
    return first.getDay();
  }, [viewYear, viewMonth]);

  const daysInMonth = useMemo(
    () => new Date(viewYear, viewMonth + 1, 0).getDate(),
    [viewYear, viewMonth],
  );

  const monthLabel = useMemo(
    () => new Date(viewYear, viewMonth, 1).toLocaleDateString(undefined, { month: 'long', year: 'numeric' }),
    [viewYear, viewMonth],
  );

  // Offer deflection intervention once customer selects a date (per mockup this
  // appears as an interstitial modal).
  useEffect(() => {
    if (!onDeflect) return;
    if (state.date && !state.time && state.service?.code === 'DDA') {
      // Deflection logic for DDA; fire once via a flag in session storage.
      const key = `rae-deflect-seen-${state.service.code}`;
      if (typeof window !== 'undefined' && !window.sessionStorage.getItem(key)) {
        window.sessionStorage.setItem(key, '1');
        onDeflect();
      }
    }
  }, [state.date, state.time, state.service, onDeflect]);

  function handleDayKey(e: KeyboardEvent<HTMLButtonElement>, currentIso: string): void {
    const current = new Date(`${currentIso}T12:00:00`);
    let target: Date | null = null;
    if (e.key === 'ArrowRight') target = new Date(current); target?.setDate(current.getDate() + 1);
    if (e.key === 'ArrowLeft') { target = new Date(current); target.setDate(current.getDate() - 1); }
    if (e.key === 'ArrowDown') { target = new Date(current); target.setDate(current.getDate() + 7); }
    if (e.key === 'ArrowUp') { target = new Date(current); target.setDate(current.getDate() - 7); }
    if (target) {
      e.preventDefault();
      const iso = toIso(target.getFullYear(), target.getMonth(), target.getDate());
      const btn = gridRef.current?.querySelector<HTMLButtonElement>(`button[data-iso='${iso}']`);
      btn?.focus();
    }
  }

  return (
    <main className="rae-wizard__main">
      <button type="button" className="rae-wizard__back" onClick={onBack}>← Back</button>
      <h1 className="rae-wizard__h1">Where and when?</h1>
      <p className="rae-wizard__lead">Pick the branch, date, and time that works for you.</p>

      <section className="rae-card" aria-labelledby="rae-branch-h2">
        <h2 id="rae-branch-h2">Choose a location</h2>
        <div className="rae-branches" role="radiogroup" aria-labelledby="rae-branch-h2">
          {branches.map((b) => {
            const isSelected = state.branch?.branchCode === b.branchCode;
            return (
              <button
                key={b.branchCode}
                type="button"
                role="radio"
                aria-checked={isSelected}
                className={`rae-branch ${isSelected ? 'is-selected' : ''}`.trim()}
                onClick={() => dispatch({ type: 'setBranch', branch: b })}
              >
                <span className="rae-branch__check" aria-hidden>✓</span>
                <span className="rae-branch__name">{b.branchName}</span>
                <span className="rae-branch__addr">
                  {b.addressLine1}
                  <br />
                  {b.city}, {b.state} {b.zip}
                </span>
                {b.distanceMiles !== undefined && (
                  <span className="rae-branch__dist">{b.distanceMiles.toFixed(1)} miles away</span>
                )}
              </button>
            );
          })}
        </div>
      </section>

      <section className="rae-card" aria-labelledby="rae-when-h2">
        <h2 id="rae-when-h2">Choose a date &amp; time</h2>
        <div className="rae-cal-wrap">
          <div>
            <div className="rae-cal__head">
              <span className="mo">{monthLabel}</span>
              <div className="rae-cal__nav">
                <button
                  type="button"
                  aria-label="Previous month"
                  onClick={() => {
                    if (viewMonth === 0) { setViewMonth(11); setViewYear(viewYear - 1); }
                    else setViewMonth(viewMonth - 1);
                  }}
                >‹</button>
                <button
                  type="button"
                  aria-label="Next month"
                  onClick={() => {
                    if (viewMonth === 11) { setViewMonth(0); setViewYear(viewYear + 1); }
                    else setViewMonth(viewMonth + 1);
                  }}
                >›</button>
              </div>
            </div>
            <div className="rae-cal__grid" role="grid" aria-label="Appointment dates" ref={gridRef}>
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((dow) => (
                <div key={dow} className="rae-cal__cell is-dow" role="columnheader">{dow}</div>
              ))}
              {Array.from({ length: firstDayOffset }).map((_, i) => (
                <div key={`p${i}`} className="rae-cal__cell is-dim" aria-hidden />
              ))}
              {Array.from({ length: daysInMonth }).map((_, i) => {
                const day = i + 1;
                const iso = toIso(viewYear, viewMonth, day);
                const avail = hasSlotsOn(iso);
                const isSelected = state.date === iso;
                return (
                  <button
                    key={iso}
                    type="button"
                    data-iso={iso}
                    className={[
                      'rae-cal__cell',
                      avail ? 'is-avail' : 'is-dim',
                      avail ? 'has-slot' : '',
                      isSelected ? 'is-selected' : '',
                    ].filter(Boolean).join(' ')}
                    disabled={!avail}
                    aria-selected={isSelected}
                    aria-label={`${new Date(`${iso}T12:00:00`).toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}${avail ? ', has slots' : ', no slots'}`}
                    onClick={() => avail && dispatch({ type: 'setDateTime', date: iso, time: state.time ?? '' })}
                    onKeyDown={(e) => avail && handleDayKey(e, iso)}
                  >
                    {day}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="rae-time-groups">
            <h3>{state.date ? new Date(`${state.date}T12:00:00`).toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' }) : 'Pick a date'}</h3>

            {(['Morning', 'Afternoon', 'Evening'] as const).map((group, idx) => {
              const slots = idx === 0 ? MORNING : idx === 1 ? AFTERNOON : EVENING;
              return (
                <div key={group}>
                  <h3>{group}</h3>
                  <div className="rae-chip-row" role="group" aria-label={`${group} time slots`}>
                    {slots.map((t) => {
                      const unavail = UNAVAILABLE.has(t) || !state.date;
                      const limited = LIMITED.has(t);
                      const isSelected = state.time === t && !!state.date;
                      const label = `${formatTime12(t)}${limited ? ' — few left' : ''}`;
                      return (
                        <button
                          key={t}
                          type="button"
                          className={[
                            'rae-t-chip',
                            isSelected ? 'is-selected' : '',
                            limited && !unavail ? 'is-limited' : '',
                          ].filter(Boolean).join(' ')}
                          disabled={unavail}
                          aria-disabled={unavail}
                          aria-label={label}
                          aria-pressed={isSelected}
                          onClick={() => state.date && dispatch({ type: 'setDateTime', date: state.date, time: t })}
                        >
                          {formatTime12(t)}
                          {limited && !unavail && <span aria-hidden> · Few left</span>}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}

            <div className="rae-tz-note">
              <span aria-hidden>🌎</span>
              All times shown in <strong>America/Chicago (Central)</strong>
            </div>
          </div>
        </div>
      </section>

      <div className="rae-wizard__actions">
        <Button variant="ghost" size="lg" onClick={onBack}>← Back</Button>
        <Button
          variant="primary"
          size="lg"
          disabled={!state.branch || !state.date || !state.time}
          onClick={onContinue}
        >
          Continue →
        </Button>
      </div>
    </main>
  );
}
